import React, { useState, useMemo } from 'react';
import { X, Printer } from 'lucide-react';

const PrintProductsModal = ({ 
  isOpen, 
  onClose, 
  products = [], 
  productos = [],
  clientes = [],
  orders = [],
  temporada = null,
  currentClientType = 'all'
}) => {
  const [filterType, setFilterType] = useState(currentClientType);

  // Filter products based on selected client type
  const filteredProducts = useMemo(() => {
    if (filterType === 'all') {
      return products;
    }

    // Get client IDs for the selected type
    const filteredClientIds = new Set(
      clientes
        .filter(cliente => {
          const pais = cliente.pais?.toUpperCase();
          const isExportacion = pais === 'USA' || pais === 'CANADA' || pais === 'CANADÁ';
          
          if (filterType === 'exportacion') {
            return isExportacion;
          } else if (filterType === 'nacional') {
            return !isExportacion;
          }
          return true;
        })
        .map(c => c.id)
    );

    // Filter orders by client type
    const filteredOrders = orders.filter(order => {
      const clienteId = order.cliente_id || order.customerId;
      return filteredClientIds.has(clienteId);
    });

    // Recalculate consolidated products from filtered orders
    const productMap = new Map();
    
    filteredOrders.forEach(order => {
      if (order.detalles && Array.isArray(order.detalles)) {
        order.detalles.forEach(detalle => {
          const productoId = detalle.producto_id || detalle.producto?.id;
          const productoNombre = detalle.producto?.nombre || 'Producto sin nombre';
          const productoCode = detalle.producto?.code || '';
          const cantidadCajas = detalle.cantidad_cajas || 0;
          const precioTotal = detalle.precio_total || 0;

          if (productoId) {
            if (productMap.has(productoId)) {
              const existing = productMap.get(productoId);
              productMap.set(productoId, {
                ...existing,
                totalCajas: existing.totalCajas + cantidadCajas,
                totalMonto: existing.totalMonto + precioTotal
              });
            } else {
              productMap.set(productoId, {
                id: productoId,
                nombre: productoNombre,
                code: productoCode,
                totalCajas: cantidadCajas,
                totalMonto: precioTotal
              });
            }
          }
        });
      }
    });

    return Array.from(productMap.values()).sort((a, b) => b.totalCajas - a.totalCajas);
  }, [products, filterType, clientes, orders]);

  // Enrich products with weight and volume
  const enrichedProducts = useMemo(() => {
    return filteredProducts.map(product => {
      const productoData = productos.find(p => p.id === product.id);
      const pesoTotalKg = productoData?.peso_total_caja_kg || 0;
      const volumenM3 = productoData?.volumen_m3 || 0;
      const totalCajas = product.totalCajas || 0;
      const totalMonto = product.totalMonto || 0;
      
      return {
        ...product,
        totalCajas,
        totalMonto,
        totalWeight: totalCajas * pesoTotalKg,
        totalVolume: totalCajas * volumenM3
      };
    });
  }, [filteredProducts, productos]);

  // Calculate totals
  const totals = enrichedProducts.reduce((acc, product) => ({
    totalCajas: acc.totalCajas + product.totalCajas,
    totalMonto: acc.totalMonto + product.totalMonto,
    totalWeight: acc.totalWeight + product.totalWeight,
    totalVolume: acc.totalVolume + product.totalVolume
  }), { totalCajas: 0, totalMonto: 0, totalWeight: 0, totalVolume: 0 });

  const handlePrint = () => {
    window.print();
  };

  const getFilterLabel = () => {
    switch (filterType) {
      case 'exportacion':
        return 'Exportación (USA/Canadá)';
      case 'nacional':
        return 'Nacional (Chile)';
      default:
        return 'Todos';
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content print-modal" onClick={(e) => e.stopPropagation()}>
          {/* Modal Header - Hidden when printing */}
          <div className="modal-header no-print">
            <h2 className="modal-title">Imprimir Listado de Productos</h2>
            <button className="close-button" onClick={onClose}>
              <X size={20} />
            </button>
          </div>

          {/* Filter Options - Hidden when printing */}
          <div className="filter-section no-print">
            <label className="filter-label">Tipo de Cliente:</label>
            <div className="filter-buttons">
              <button
                className={`filter-button ${filterType === 'all' ? 'active' : ''}`}
                onClick={() => setFilterType('all')}
              >
                Todos
              </button>
              <button
                className={`filter-button ${filterType === 'exportacion' ? 'active' : ''}`}
                onClick={() => setFilterType('exportacion')}
              >
                Exportación
              </button>
              <button
                className={`filter-button ${filterType === 'nacional' ? 'active' : ''}`}
                onClick={() => setFilterType('nacional')}
              >
                Nacional
              </button>
            </div>
          </div>

          {/* Print Preview */}
          <div className="print-preview">
            {/* Print Header - Only visible when printing */}
            <div className="print-header">
              <h1 className="print-company">Patagonia Vital</h1>
              <h2 className="print-title">Listado de Productos</h2>
              <div className="print-info">
                {temporada && (
                  <p>Temporada {temporada.year} - Semana {temporada.week}</p>
                )}
                <p>Tipo: {getFilterLabel()}</p>
                <p className="print-date">Fecha: {new Date().toLocaleDateString('es-CL')}</p>
              </div>
            </div>

            {/* Products Table */}
            {enrichedProducts.length === 0 ? (
              <div className="empty-message">
                <p>No hay productos para imprimir</p>
              </div>
            ) : (
              <table className="print-table">
                <thead>
                  <tr>
                    <th className="th-product">Producto</th>
                    <th className="th-code">Código</th>
                    <th className="th-boxes">Cajas</th>
                    <th className="th-weight">Peso (kg)</th>
                    <th className="th-volume">Volumen (m³)</th>
                    <th className="th-amount">Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {enrichedProducts.map((product) => (
                    <tr key={product.id}>
                      <td className="td-product">{product.nombre}</td>
                      <td className="td-code">{product.code}</td>
                      <td className="td-boxes">{product.totalCajas}</td>
                      <td className="td-weight">
                        {(product.totalWeight || 0).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                      </td>
                      <td className="td-volume">
                        {(product.totalVolume || 0).toFixed(3).replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                      </td>
                      <td className="td-amount">
                        ${(product.totalMonto || 0).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="totals-row">
                    <td colSpan="2" className="td-totals-label">Totales:</td>
                    <td className="td-boxes">{totals.totalCajas}</td>
                    <td className="td-weight">
                      {(totals.totalWeight || 0).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                    </td>
                    <td className="td-volume">
                      {(totals.totalVolume || 0).toFixed(3).replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                    </td>
                    <td className="td-amount">
                      ${(totals.totalMonto || 0).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                    </td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>

          {/* Modal Footer - Hidden when printing */}
          <div className="modal-footer no-print">
            <button className="btn btn-secondary" onClick={onClose}>
              Cerrar
            </button>
            <button 
              className="btn btn-primary" 
              onClick={handlePrint}
              disabled={enrichedProducts.length === 0}
            >
              <Printer size={18} />
              Imprimir
            </button>
          </div>
        </div>
      </div>

      <style jsx>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: var(--spacing-4);
        }

        .modal-content {
          background: white;
          border-radius: var(--radius-lg);
          max-width: 1200px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: var(--spacing-6);
          border-bottom: 1px solid var(--gray-200);
          background: white;
          position: relative;
          z-index: 1;
        }

        .modal-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--gray-900);
          margin: 0;
        }

        .close-button {
          padding: var(--spacing-2);
          border: none;
          background: transparent;
          color: var(--gray-500);
          cursor: pointer;
          border-radius: var(--radius-md);
          transition: all 0.2s;
        }

        .close-button:hover {
          background-color: var(--gray-100);
          color: var(--gray-700);
        }

        .filter-section {
          padding: var(--spacing-6);
          border-bottom: 1px solid var(--gray-200);
        }

        .filter-label {
          display: block;
          font-weight: 600;
          color: var(--gray-700);
          margin-bottom: var(--spacing-3);
        }

        .filter-buttons {
          display: flex;
          gap: var(--spacing-3);
        }

        .filter-button {
          flex: 1;
          padding: var(--spacing-3) var(--spacing-4);
          border: 2px solid var(--gray-300);
          background: white;
          color: var(--gray-700);
          font-weight: 600;
          border-radius: var(--radius-md);
          cursor: pointer;
          transition: all 0.2s;
        }

        .filter-button:hover {
          border-color: var(--primary-blue);
          background-color: rgba(59, 130, 246, 0.05);
        }

        .filter-button.active {
          border-color: var(--primary-blue);
          background-color: var(--primary-blue);
          color: white;
        }

        .print-preview {
          padding: var(--spacing-6);
        }

        .print-header {
          display: none;
          text-align: center;
          margin-bottom: var(--spacing-6);
        }

        .print-company {
          font-size: 24px;
          font-weight: 700;
          color: #2c5f2d;
          margin: 0 0 var(--spacing-2) 0;
        }

        .print-title {
          font-size: 20px;
          font-weight: 600;
          color: var(--gray-900);
          margin: 0 0 var(--spacing-4) 0;
        }

        .print-info {
          font-size: 14px;
          color: var(--gray-600);
        }

        .print-info p {
          margin: var(--spacing-1) 0;
        }

        .print-date {
          font-style: italic;
        }

        .empty-message {
          text-align: center;
          padding: var(--spacing-12);
          color: var(--gray-600);
        }

        .print-table {
          width: 100%;
          border-collapse: collapse;
          margin-top: var(--spacing-4);
        }

        .print-table thead {
          background-color: var(--gray-50);
        }

        .print-table th {
          padding: var(--spacing-3) var(--spacing-4);
          text-align: left;
          font-size: var(--font-size-sm);
          font-weight: 600;
          color: var(--gray-700);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border-bottom: 2px solid var(--gray-300);
        }

        .print-table td {
          padding: var(--spacing-3) var(--spacing-4);
          border-bottom: 1px solid var(--gray-200);
          color: var(--gray-900);
        }

        .print-table tbody tr:hover {
          background-color: var(--gray-50);
        }

        .th-boxes,
        .th-weight,
        .th-volume,
        .th-amount,
        .td-boxes,
        .td-weight,
        .td-volume,
        .td-amount {
          text-align: right;
        }

        .td-code {
          font-family: monospace;
          font-size: var(--font-size-sm);
          color: var(--gray-600);
        }

        .totals-row {
          background-color: var(--gray-100);
          font-weight: 700;
          border-top: 2px solid var(--gray-300);
        }

        .td-totals-label {
          text-align: right;
          font-weight: 700;
          color: var(--gray-900);
          text-transform: uppercase;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: var(--spacing-3);
          padding: var(--spacing-6);
          border-top: 1px solid var(--gray-200);
        }

        .btn {
          display: flex;
          align-items: center;
          gap: var(--spacing-2);
          padding: var(--spacing-3) var(--spacing-5);
          border: none;
          border-radius: var(--radius-md);
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-secondary {
          background-color: var(--gray-100);
          color: var(--gray-700);
        }

        .btn-secondary:hover {
          background-color: var(--gray-200);
        }

        .btn-primary {
          background-color: var(--primary-blue);
          color: white;
        }

        .btn-primary:hover {
          background-color: #2563eb;
        }

        .btn-primary:disabled {
          background-color: var(--gray-300);
          color: var(--gray-500);
          cursor: not-allowed;
        }

        /* Print Styles */
        @media print {
          /* Hide everything on the page */
          body * {
            visibility: hidden;
          }

          /* Show only the modal content */
          .modal-overlay,
          .modal-overlay * {
            visibility: visible;
          }

          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: white;
            padding: 0;
            margin: 0;
            z-index: 9999;
          }

          .modal-content {
            position: static;
            max-width: none;
            max-height: none;
            box-shadow: none;
            border-radius: 0;
            margin: 0;
            padding: 0;
            overflow: visible;
          }

          .no-print {
            display: none !important;
          }

          .print-header {
            display: block !important;
            page-break-after: avoid;
          }

          .print-preview {
            padding: 0;
          }

          .print-table {
            margin-top: var(--spacing-6);
            page-break-inside: auto;
          }

          .print-table thead {
            display: table-header-group;
          }

          .print-table tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }

          .print-table th,
          .print-table td {
            border: 1px solid #333;
            padding: 8px;
          }

          .print-table tbody tr:hover {
            background-color: transparent;
          }

          .totals-row {
            page-break-inside: avoid;
          }

          @page {
            margin: 1.5cm;
            size: A4 portrait;
          }

          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }

          /* Show all columns when printing */
          .th-code,
          .td-code,
          .th-weight,
          .td-weight,
          .th-volume,
          .td-volume {
            display: table-cell !important;
          }

          /* Adjust table layout for print */
          .print-table {
            width: 100%;
            table-layout: fixed;
          }

          .th-product,
          .td-product {
            width: 28%;
          }

          .th-code,
          .td-code {
            width: 20%;
          }

          .th-boxes,
          .td-boxes,
          .th-weight,
          .td-weight,
          .th-volume,
          .td-volume,
          .th-amount,
          .td-amount {
            width: 10.4%;
          }
        }

        /* Responsive */
        @media (max-width: 768px) {
          .modal-content {
            max-height: 95vh;
          }

          .filter-buttons {
            flex-direction: column;
          }

          .print-table {
            font-size: var(--font-size-sm);
          }

          .print-table th,
          .print-table td {
            padding: var(--spacing-2) var(--spacing-3);
          }

          .th-code,
          .td-code,
          .th-weight,
          .td-weight,
          .th-volume,
          .td-volume {
            display: none;
          }
        }
      `}</style>
    </>
  );
};

export default PrintProductsModal;
